/**
 * ModularNavbar - TypeScript ES6 Module Migration
 * Custom navbar collapse/dropdown behavior without Bootstrap dependency.
 * Migrated from IIFE pattern (modular-navbar.js) to typed ES6 class.
 */

const MOBILE_BREAKPOINT = 576;

export class ModularNavbar {
    private navbarCollapse: HTMLElement | null = null;
    private navbarToggler: HTMLElement | null = null;

    public initialize(): void {
        this.navbarCollapse = document.querySelector('.navbar-collapse');
        this.navbarToggler = document.querySelector('.navbar-toggler');

        if (!this.navbarCollapse || !this.navbarToggler) return;

        this.navbarCollapse.classList.remove('show');
        this.navbarToggler.setAttribute('aria-expanded', 'false');

        this.navbarToggler.addEventListener('click', (e: Event) => {
            e.preventDefault();
            this.toggleMobileMenu();
        });

        this.setupDropdowns();
        this.setupAutoCollapse();
        this.setupClickOutside();
    }

    private toggleMobileMenu(): void {
        if (!this.navbarCollapse || !this.navbarToggler) return;

        const isExpanded: boolean = this.navbarCollapse.classList.contains('show');
        this.navbarCollapse.classList.toggle('show', !isExpanded);
        this.navbarToggler.setAttribute('aria-expanded', String(!isExpanded));
    }

    private setupDropdowns(): void {
        const toggles: NodeListOf<HTMLElement> = document.querySelectorAll('.dropdown-toggle');
        toggles.forEach((toggle: HTMLElement) => {
            toggle.addEventListener('click', (e: Event) => {
                e.preventDefault();
                const menu: Element | null = toggle.nextElementSibling;
                if (!menu || !menu.classList.contains('dropdown-menu')) return;

                const wasOpen: boolean = menu.classList.contains('show');
                this.closeAllDropdowns();

                if (!wasOpen) {
                    menu.classList.add('show');
                }
            });
        });
    }

    private setupAutoCollapse(): void {
        if (!this.navbarCollapse || !this.navbarToggler) return;

        const links: NodeListOf<HTMLElement> = this.navbarCollapse.querySelectorAll(
            '.nav-link:not(.dropdown-toggle), .dropdown-item'
        );

        links.forEach((link: HTMLElement) => {
            link.addEventListener('click', () => {
                if (window.innerWidth < MOBILE_BREAKPOINT) {
                    this.navbarCollapse!.classList.remove('show');
                    this.navbarToggler!.setAttribute('aria-expanded', 'false');
                }
                this.closeAllDropdowns();
            });
        });
    }

    private setupClickOutside(): void {
        document.addEventListener('click', (e: MouseEvent) => {
            const target: Node = e.target as Node;

            if (window.innerWidth < MOBILE_BREAKPOINT &&
                this.navbarToggler && !this.navbarToggler.contains(target) &&
                this.navbarCollapse && !this.navbarCollapse.contains(target) &&
                this.navbarCollapse.classList.contains('show')) {
                this.navbarCollapse.classList.remove('show');
                this.navbarToggler.setAttribute('aria-expanded', 'false');
            }

            if (!(e.target as HTMLElement).closest('.dropdown') &&
                !(e.target as HTMLElement).closest('.btn-group')) {
                this.closeAllDropdowns();
            }
        });
    }

    private closeAllDropdowns(): void {
        document.querySelectorAll<HTMLElement>('.dropdown-menu').forEach((menu: HTMLElement) => {
            menu.classList.remove('show');
            menu.style.display = '';
        });
    }
}

// Auto-initialize
const navbar: ModularNavbar = new ModularNavbar();
document.addEventListener('DOMContentLoaded', () => {
    navbar.initialize();
});

export default navbar;
